import { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { communicationsJournalApi } from "@services/api/communications";
import type {
  CommsJournalItem,
  CommsChannel,
  CommsDirection,
  CommsOrg,
} from "@services/api/communications";
import { showNotification } from "context/notification.service";
import { useDataStore } from "@store/user_org";

const PAGE_SIZE = 50;

interface Filters {
  channel?: CommsChannel;
  direction?: CommsDirection;
  status?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

function ChannelBadge({ channel }: { channel: CommsChannel }) {
  const isWa = channel === "whatsapp";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-h5 font-medium ${
        isWa ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
      }`}
    >
      {isWa ? "WhatsApp" : "Email"}
    </span>
  );
}

function DirectionBadge({ direction }: { direction: CommsDirection }) {
  const isIn = direction === "inbound";
  return (
    <span className={`inline-flex items-center gap-1 text-h5 font-medium ${isIn ? "text-purple-600" : "text-neutral-600"}`}>
      {isIn ? "↓ Reçu" : "↑ Envoyé"}
    </span>
  );
}

const STATUS_STYLE: Record<string, string> = {
  sent: "bg-neutral-100 text-neutral-600",
  delivered: "bg-sky-100 text-sky-700",
  read: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  received: "bg-purple-100 text-purple-700",
  queued: "bg-amber-100 text-amber-700",
};

const STATUS_LABEL: Record<string, string> = {
  sent: "Envoyé",
  delivered: "Distribué",
  read: "Lu",
  failed: "Échec",
  received: "Reçu",
  queued: "En file",
};

function StatusBadge({ status, reason }: { status?: string | null; reason?: string | null }) {
  if (!status) return <span className="text-neutral-400">—</span>;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span
        title={reason || undefined}
        className={`inline-block px-2 py-0.5 rounded-full text-h5 font-medium ${STATUS_STYLE[status] || "bg-neutral-100 text-neutral-500"}`}
      >
        {STATUS_LABEL[status] || status}
      </span>
      {status === "failed" && reason && (
        <span className="text-h5 text-red-600 whitespace-nowrap">{reason}</span>
      )}
    </span>
  );
}

function JournalRow({ item }: { item: CommsJournalItem }) {
  const content = item.subject
    ? `${item.subject}${item.body ? " — " + item.body : ""}`
    : item.body || "";
  return (
    <tr className="border-t border-neutral-100 align-top">
      <td className="pr-3 py-2 pl-3 whitespace-nowrap text-neutral-500 text-h5">
        {item.created_at ? dayjs(item.created_at).format("DD/MM/YY HH:mm") : "—"}
      </td>
      <td className="pr-3 py-2"><ChannelBadge channel={item.channel} /></td>
      <td className="pr-3 py-2"><DirectionBadge direction={item.direction} /></td>
      <td className="pr-3 py-2"><StatusBadge status={item.status} reason={item.failed_reason} /></td>
      <td className="pr-3 py-2 whitespace-nowrap font-mono text-h5 text-neutral-600">{item.peer || "—"}</td>
      <td className="pr-3 py-2 text-h4 text-neutral-800 max-w-md">
        <div className="line-clamp-3 whitespace-pre-wrap break-words">{content || <span className="text-neutral-400">(vide)</span>}</div>
      </td>
    </tr>
  );
}

export function Communications() {
  const ownOrgId = useDataStore((s) => s.orgData?.id);
  const [orgs, setOrgs] = useState<CommsOrg[]>([]);
  const [orgId, setOrgId] = useState<string>("");
  const [filters, setFilters] = useState<Filters>({});
  const [items, setItems] = useState<CommsJournalItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  // Load the org list once, default the selection to the admin's own org.
  useEffect(() => {
    communicationsJournalApi
      .listOrgs()
      .then((list) => {
        setOrgs(list);
        setOrgId((cur) => cur || ownOrgId || list[0]?.id || "");
      })
      .catch(() => {
        // Fall back to own org if the list can't be loaded
        setOrgId((cur) => cur || ownOrgId || "");
      });
  }, [ownOrgId]);

  // Local (uncommitted) filter inputs
  const [search, setSearch] = useState("");
  const [channel, setChannel] = useState<"" | CommsChannel>("");
  const [direction, setDirection] = useState<"" | CommsDirection>("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchPage = useCallback(
    async (newOffset: number) => {
      if (!orgId) return;
      setLoading(true);
      try {
        const res = await communicationsJournalApi.list({
          org_id: orgId,
          ...filters,
          limit: PAGE_SIZE,
          offset: newOffset,
        });
        setItems(res.items);
        setTotal(res.total);
        setOffset(newOffset);
      } catch (e: any) {
        showNotification({
          type: "error",
          message: e?.response?.data?.detail || "Erreur lors du chargement du journal",
        });
      } finally {
        setLoading(false);
      }
    },
    [filters, orgId],
  );

  useEffect(() => {
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, orgId]);

  const applyFilters = () => {
    setFilters({
      search: search.trim() || undefined,
      channel: channel || undefined,
      direction: direction || undefined,
      status: status || undefined,
      // Convert date inputs (yyyy-mm-dd) to ISO bounds
      date_from: dateFrom ? dayjs(dateFrom).startOf("day").toISOString() : undefined,
      date_to: dateTo ? dayjs(dateTo).endOf("day").toISOString() : undefined,
    });
  };

  const clearFilters = () => {
    setSearch("");
    setChannel("");
    setDirection("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    setFilters({});
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="p-6 flex flex-col gap-4 w-full h-full overflow-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-h1 font-semibold text-neutral-800">Communications</h1>
          <p className="text-h4 text-neutral-500">
            Journal de tous les messages WhatsApp et emails échangés avec les clients (envoyés et reçus).
          </p>
        </div>
        <div className="flex flex-col">
          <label className="text-h5 text-neutral-500 mb-1">Organisation</label>
          <select
            className="border border-neutral-300 rounded-lg px-3 py-2 text-h4 font-medium min-w-56"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {orgs.length === 0 && <option value={orgId}>Chargement…</option>}
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex flex-col">
          <label className="text-h5 text-neutral-500 mb-1">Recherche</label>
          <input
            type="text"
            className="border border-neutral-300 rounded-lg px-2 py-1 text-h4 w-56"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            placeholder="Texte, numéro, email…"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-h5 text-neutral-500 mb-1">Canal</label>
          <select className="border border-neutral-300 rounded-lg px-2 py-1 text-h4 w-36" value={channel} onChange={(e) => setChannel(e.target.value as "" | CommsChannel)}>
            <option value="">— Tous —</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-h5 text-neutral-500 mb-1">Sens</label>
          <select className="border border-neutral-300 rounded-lg px-2 py-1 text-h4 w-36" value={direction} onChange={(e) => setDirection(e.target.value as "" | CommsDirection)}>
            <option value="">— Tous —</option>
            <option value="outbound">Envoyé</option>
            <option value="inbound">Reçu</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-h5 text-neutral-500 mb-1">Statut</label>
          <select className="border border-neutral-300 rounded-lg px-2 py-1 text-h4 w-32" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">— Tous —</option>
            <option value="sent">Envoyé</option>
            <option value="delivered">Distribué</option>
            <option value="read">Lu</option>
            <option value="failed">Échec</option>
            <option value="received">Reçu</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-h5 text-neutral-500 mb-1">Du</label>
          <input type="date" className="border border-neutral-300 rounded-lg px-2 py-1 text-h4" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="flex flex-col">
          <label className="text-h5 text-neutral-500 mb-1">Au</label>
          <input type="date" className="border border-neutral-300 rounded-lg px-2 py-1 text-h4" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <Button label="Filtrer" onClick={applyFilters} variant="small" />
        <SecondaryButton label="Réinitialiser" onClick={clearFilters} variant="small" />
      </div>

      {/* Table */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden">
        <div className="max-h-[65vh] overflow-y-auto">
          <table className="w-full text-h4">
            <thead className="bg-neutral-50 sticky top-0 z-10">
              <tr className="text-neutral-500 text-left">
                <th className="pr-3 py-2 pl-3 font-medium">Date</th>
                <th className="pr-3 py-2 font-medium">Canal</th>
                <th className="pr-3 py-2 font-medium">Sens</th>
                <th className="pr-3 py-2 font-medium">Statut</th>
                <th className="pr-3 py-2 font-medium">Contact</th>
                <th className="pr-3 py-2 font-medium">Message</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <JournalRow key={it.id} item={it} />
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-neutral-400 py-8 text-h4">
                    Aucun message trouvé
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center text-neutral-400 py-8 text-h4">
                    Chargement…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <div className="text-h5 text-neutral-500">
          {total > 0 ? `${pageStart}–${pageEnd} sur ${total}` : "0 résultat"}
        </div>
        <div className="flex gap-2">
          <SecondaryButton
            label="Précédent"
            onClick={() => fetchPage(Math.max(0, offset - PAGE_SIZE))}
            variant="small"
            disabled={loading || offset === 0}
          />
          <SecondaryButton
            label="Suivant"
            onClick={() => fetchPage(offset + PAGE_SIZE)}
            variant="small"
            disabled={loading || offset + PAGE_SIZE >= total}
          />
        </div>
      </div>
    </div>
  );
}
